import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import "./DigitalTicket.css";
import { formatDateWithOrdinal } from "../../helper/utils";

const encodeQRData = ({
  guestId,
  eventId,
  guestName,
  eventTitle,
  eventDate,
  eventTime,
  invitationStatus = "accepted",
  numberOfGuests = 1,
  eventEndDate,
  eventEndTime,
  multipleDates,
}) => {
  try {
    const payload = {
      eventId,
      guestId,
      eventTitle,
      guestName,
      eventDate,
      eventTime,
      invitationStatus,
      numberOfGuests,
      eventEndDate: eventEndDate || eventDate,
      eventEndTime: eventEndTime || eventTime,
      ...(multipleDates ? { multipleDates } : {}),
      timestamp: Date.now(),
    };

    const jsonString = JSON.stringify(payload);

    const utf8Bytes = new TextEncoder().encode(jsonString);
    const binaryString = Array.from(utf8Bytes)
      .map((b) => String.fromCharCode(b))
      .join("");
    const base64Encoded = btoa(binaryString);

    return `NIMBLEBASH://checkin/${base64Encoded}`;
  } catch (error) {
    return JSON.stringify({
      eventId,
      guestId,
      guestName,
      eventTitle,
      eventDate,
      eventTime,
    });
  }
};

const DigitalTicket = ({ isOpen, onClose, ticketData, event }) => {
  const ticketRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!isOpen) return null;

  const {
    guestName,
    guestId,
    eventId,
    eventTitle,
    eventDate,
    eventTime,
    eventLocation,
    ticketType,
    isCheckedIn,
    invitationStatus,
  } = ticketData;

  const formatDateMobile = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  const qrCodeData = encodeQRData({
    guestId,
    eventId,
    guestName,
    eventTitle,
    eventDate,
    eventTime,
    invitationStatus: invitationStatus,
    numberOfGuests: ticketData.numberOfGuests || 1,
    eventEndDate: event?.endDate,
    eventEndTime: event?.endTime,
    multipleDates: event?.multipleDates?.map((md) => ({
      date: md.date,
      time: md.time,
    })),
  });

  const handleDownloadTicket = async () => {
    try {
      setIsDownloading(true);

      const ticketElement = ticketRef.current;
      if (!ticketElement) return;

      const canvas = await html2canvas(ticketElement, {
        backgroundColor: "#FFFFFF",
        scale: 2,
        logging: false,
        useCORS: true,
      });

      canvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = `${eventTitle.replace(
          /[^a-zA-Z0-9]/g,
          "_"
        )}_Ticket_${Date.now()}.png`;

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        alert("Ticket downloaded successfully!");
      }, "image/png");
    } catch (error) {
      console.error("Error downloading ticket:", error);
      alert("Failed to download ticket. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="digital-ticket-overlay" onClick={onClose}>
      <div
        className="digital-ticket-modal-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={ticketRef} className="digital-ticket-download-content">
          <div className="digital-ticket-header-bar"></div>

          <div className="digital-ticket-body">
            <div className="digital-ticket-logo">
              <img src="/images/iconNew2.png" alt="Logo" />
            </div>

            <div className="digital-ticket-title-section">
              <h1 className="digital-ticket-event-title-main">
                {eventTitle.toUpperCase()}
              </h1>
            </div>

            <div className="digital-ticket-divider"></div>

            <div className="digital-ticket-details-grid">
              <div className="digital-ticket-detail-row-grid">
                <div className="digital-ticket-detail-item-grid">
                  <div className="digital-ticket-detail-label-main">DATE</div>
                  <div className="digital-ticket-detail-value-main">
                    {formatDateMobile(eventDate)}
                  </div>
                </div>
                <div className="digital-ticket-detail-item-grid">
                  <div className="digital-ticket-detail-label-main">TIME</div>
                  <div className="digital-ticket-detail-value-main">
                    {eventTime}
                  </div>
                </div>
              </div>

              <div className="digital-ticket-detail-row-grid">
                <div className="digital-ticket-detail-item-grid digital-ticket-detail-full-grid">
                  <div className="digital-ticket-detail-label-main">VENUE</div>
                  <div className="digital-ticket-detail-value-main">
                    {eventLocation}
                  </div>
                </div>
              </div>

              <div className="digital-ticket-detail-row-grid">
                <div className="digital-ticket-detail-item-grid digital-ticket-detail-full-grid">
                  <div className="digital-ticket-detail-label-main">GUEST</div>
                  <div className="digital-ticket-detail-value-main">
                    {guestName.toUpperCase()}
                  </div>
                </div>
              </div>

              {ticketType && (
                <div className="digital-ticket-detail-row-grid">
                  <div className="digital-ticket-detail-item-grid digital-ticket-detail-full-grid">
                    <div className="digital-ticket-detail-label-main">
                      TICKET TYPE
                    </div>
                    <div className="digital-ticket-detail-value-main">
                      {ticketType.toUpperCase()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="digital-ticket-divider"></div>

            <div className="digital-ticket-qr-section-main">
              <div className="digital-ticket-qr-wrapper-main">
                <QRCodeSVG
                  value={qrCodeData}
                  size={280}
                  level="H"
                  includeMargin={false}
                />
              </div>
              <div className="digital-ticket-qr-text-main">
                Scan QR code for check-in
              </div>
              <div className="digital-ticket-qr-validity-main">
                Valid until 24 hours after event
              </div>
            </div>

            <div className="digital-ticket-divider"></div>
          </div>

          <div className="digital-ticket-footer-bar"></div>
        </div>

        <button
          className="digital-ticket-download-button-main"
          onClick={handleDownloadTicket}
          disabled={isDownloading}
        >
          {isDownloading ? "Downloading..." : "Download TICKET"}
        </button>

        <button onClick={onClose} className="digital-ticket-close-button-main">
          <span>×</span>
        </button>
      </div>
    </div>
  );
};

export default DigitalTicket;